import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Paper,
  CircularProgress
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MainLayout from '../../components/MainLayout';
import ProtectedPermissionPage from '../../components/ProtectedPermissionPage';
import useAlert from '../../hooks/useAlert';
import contractService from '../../services/contract';

const TemplateManagement: React.FC = () => {
  const { onOpen, onClose } = useAlert();
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Helper function for showing alerts with the custom alert system
  const showAlert = (message: string, severity: 'success' | 'error') => {
    onOpen({
      title: severity === 'success' ? 'Success' : 'Error',
      message,
      actions: [
        {
          children: 'OK',
          color: 'primary',
          variant: 'contained',
          onClick: onClose,
        },
      ],
    });
  };

  // Handler for file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      // Check if file is a Word document
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          selectedFile.name.endsWith('.docx')) {
        setFile(selectedFile);
      } else {
        showAlert('Please select a valid Word document (.docx)', 'error');
        // Reset the file input
        event.target.value = '';
      }
    }
  };

  // Download current template
  const handleDownloadTemplate = async () => {
    try {
      setIsDownloading(true);
      await contractService.downloadContractTemplate();
      showAlert('Template downloaded successfully', 'success');
    } catch (error) {
      console.error('Error downloading template:', error);
      showAlert('Failed to download template', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  // Upload new template
  const handleUploadTemplate = async () => {
    if (!file) {
      showAlert('Please select a file to upload', 'error');
      return;
    }

    try {
      setIsUploading(true);
      await contractService.uploadContractTemplate(file);
      showAlert('Template uploaded successfully', 'success');
      setFile(null);
      // Reset the file input
      const fileInput = document.getElementById('template-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error('Error uploading template:', error);
      showAlert('Failed to upload template', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <ProtectedPermissionPage acceptPermissions={[1]}>
      <MainLayout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Contract Template Management
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Download or upload contract templates (HR access only)
          </Typography>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Current Template
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Download the current contract template document that is used for generating new contracts.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                      onClick={handleDownloadTemplate}
                      disabled={isDownloading}
                    >
                      {isDownloading ? 'Downloading...' : 'Download Current Template'}
                    </Button>
                  </Box>

                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      Upload New Template
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Upload a new Word document (.docx) to replace the current contract template.
                    </Typography>
                    
                    <Paper
                      sx={{
                        p: 3,
                        mt: 2,
                        mb: 3,
                        border: '1px dashed rgba(0, 0, 0, 0.12)',
                        backgroundColor: 'background.paper',
                        textAlign: 'center'
                      }}
                    >
                      <input
                        id="template-file-input"
                        type="file"
                        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleFileChange}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="template-file-input">
                        <Button
                          component="span"
                          variant="outlined"
                          startIcon={<UploadFileIcon />}
                        >
                          Select Template File
                        </Button>
                      </label>
                      
                      {file && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            Selected file: {file.name}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                    
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                      onClick={handleUploadTemplate}
                      disabled={!file || isUploading}
                    >
                      {isUploading ? 'Uploading...' : 'Upload New Template'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </MainLayout>
    </ProtectedPermissionPage>
  );
};

export default TemplateManagement;